// src/components/ProfileUpdatePage/Tabs/OrgDetailsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import ImageUploader from '../layouts/ImageUploader'; // Import ImageUploader
import { get, post } from '../../utility/fetch';
import axios from 'axios';
import notification from '../../utility/notification';

const OrgDetailsTab = ({ orgDetails, setOrgDetails, onSave }) => {
    const [formData, setFormData] = useState(orgDetails);
    const [states, setStates] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingLgas, setIsLoadingLgas] = useState(false);

    // States for image uploads
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);
    const [isLogoUploading, setIsLogoUploading] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState(null);

    const [uploadedCacUrl, setUploadedCacUrl] = useState(null);
    const [cacUploadProgress, setCacUploadProgress] = useState(0);
    const [isCacUploading, setIsCacUploading] = useState(false);
    const [cacUploadError, setCacUploadError] = useState(null);

     const [uploadedLetterHeadUrl, setUploadedletterheadUrl] = useState(null);
    const [letterHeadProgress, setLetterHeadProgress] = useState(0);
    const [letterHeadUploading, setLetterHeadUploading] = useState(false);
    const [letterHeadUploadError, setLetterHeadUploadError] = useState(null);

    useEffect(() => {
        if (orgDetails) {
            setFormData(orgDetails);
        }
    }, [orgDetails]);

    // --- Handle Input Changes ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // --- Handle Select Changes ---
    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // --- Utility Function for File Upload ---
    const uploadFile = useCallback(async (file, setProgress, setIsUploading, setError) => {
        setIsUploading(true);
        setProgress(0);
        setError(null); // Clear previous errors

        const data = new FormData();
        data.append('files', file);

        const Access_token = sessionStorage.getItem('token');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/services/api/documents/connected-lab`, // Ensure this endpoint is correct
                data,
                {
                    headers: {
                        Authorization: `Bearer ${Access_token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgress(percentCompleted);
                    },
                },
            );

            if (response.data && response.data.doclink) {
                return response.data.doclink; // Return the URL of the uploaded file
            } else {
                setError(response.data?.message || "Upload failed: Invalid response.");
                return null;
            }
        } catch (error) {
            console.error("File upload error:", error);
            setError(error.response?.data?.message || error.message || "An unexpected error occurred during upload.");
            return null;
        } finally {
            setIsUploading(false);
            setProgress(0); // Reset progress after completion or error
        }
    }, []);

    // --- Handler for File Input Changes ---
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    };

    const handleFileUploadChange = async (file, type) => {
        if (!file) return;

        const supportedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        const maxSizeMB = 5;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;

        if (!supportedTypes.includes(file.type)) {
            const errorMessage = "Unsupported file type. Please upload a JPEG, PNG, or PDF.";
            if (type === 'logo') setLogoUploadError(errorMessage);
            if (type === 'cac') setCacUploadError(errorMessage);
            if (type === 'letterhead') setLetterHeadUploadError(errorMessage);
            return;
        }

        if (file.size > maxSizeBytes) {
            const errorMessage = `File size exceeds ${maxSizeMB}MB limit.`;
            if (type === 'logo') setLogoUploadError(errorMessage);
            if (type === 'cac') setCacUploadError(errorMessage);
            if (type === 'letterhead') setLetterHeadUploadError(errorMessage);
            return;
        }

        let uploadedLink = null;
        if (type === 'logo') {
            uploadedLink = await uploadFile(file, setLogoUploadProgress, setIsLogoUploading, setLogoUploadError);
            if (uploadedLink) {
                setUploadedLogoUrl(uploadedLink);
                setFormData((prev) => ({ ...prev, logoPath: uploadedLink }));
            }
        } else if (type === 'cac') {
            uploadedLink = await uploadFile(file, setCacUploadProgress, setIsCacUploading, setCacUploadError);
            if (uploadedLink) {
                setUploadedCacUrl(uploadedLink);
                setFormData((prev) => ({ ...prev, cacPath: uploadedLink }));
            }
        } else if (type === 'letterhead') {
            try {
              setLetterHeadUploading(true);
              setLetterHeadProgress(0);
              setLetterHeadUploadError(null);

              // Convert file to base64
              const base64String = await fileToBase64(file);
              
              setLetterHeadProgress(100);
              setUploadedletterheadUrl(base64String);
              setFormData((prev) => ({ ...prev, logoBase64: base64String }));
            } catch (error) {
              console.error("Error converting file to base64:", error);
              setLetterHeadUploadError("Failed to process letterhead image");
            } finally {
              setLetterHeadUploading(false);
              setLetterHeadProgress(0);
            }
        }
    };

    const handleRemoveImage = (type) => {
        if (type === 'logo') {
            setUploadedLogoUrl(null);
            setFormData((prev) => ({ ...prev, logoPath: null }));
        } else if (type === 'cac') {
            setUploadedCacUrl(null);
            setFormData((prev) => ({ ...prev, cacPath: null }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    useEffect(() => {
        getStates();
    }, []);

     // --- Fetch States Data ---
        const getStates = useCallback(async () => {
            try {
                const response = await axios.get(`https://api.greenzonetechnologies.com.ng/connectedhealthlabapi/api/healthcareprovider/getstates`);
                console.log(response)
                const mappedStates = response?.data?.map((state) => ({
                    name: state?.state,
                    value: state?.id,
                })) || [];
                setStates([{ name: "Select State", value: 0 }, ...mappedStates]);
            } catch (error) {
                console.error("Error fetching states:", error);
                notification({ title: "Error", message: "Failed to load states.", type: "danger" });
            }
        }, []); // Memoize: runs only once on mount
    
        // --- Fetch LGAs Data ---
        const getLgas = useCallback(async (stateId) => {
            if (!stateId || stateId === 0) {
                setLgas([{ name: "Select LGA", value: 0 }]); // Ensure empty array for LGAs if no state or invalid state
                return;
            }
            try {
                const response = await axios.get(`https://api.greenzonetechnologies.com.ng/connectedhealthlabapi/api/healthcareprovider/getlgasbystateid/id/${stateId}`);
                console.log(response)
                const mappedLgas = response?.data?.map((lga) => ({
                    name: lga?.lga,
                    value: lga?.id,
                })) || [];
                setLgas([{ name: "Select LGA", value: 0 }, ...mappedLgas]);
            } catch (error) {
                console.error("Error fetching LGAs:", error);
                notification({ title: "Error", message: "Failed to load LGAs.", type: "danger" });
            }
        }, []); // Memoize: runs only when stateId changes

    return (
        <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-group">
                <label htmlFor="name">Organization Name</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData?.name || ''}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-group">
                <label htmlFor="brandName">Brand Name</label>
                <input
                    type="text"
                    id="brandName"
                    name="brandName"
                    value={formData?.brandName || ''}
                    onChange={handleChange}
                />
            </div>
            <div className="form-group">
                <label htmlFor="location">Location Address</label>
                <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData?.location || ''}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-group">
                <label htmlFor="stateId">State</label>
                <select
                    id="stateId"
                    name="stateId"
                    value={formData?.stateId || 0}
                    onChange={(e) => {
                        handleSelectChange(e);
                        getLgas(e.target.value);
                    }}
                    disabled={isLoadingStates}
                    required
                >
                    <option value={0}>Select State</option>
                    {states.map((state) => (
                        <option key={state.value} value={state.value}>
                            {state.name}
                        </option>
                    ))}
                </select>
                {isLoadingStates && <p className="loading-message">Loading states...</p>}
            </div>
            <div className="form-group">
                <label htmlFor="lgaId">LGA</label>
                <select
                    id="lgaId"
                    name="lgaId"
                    value={formData?.lgaId || 0}
                    onChange={handleSelectChange}
                    disabled={!formData?.stateId || isLoadingLgas}
                    required
                >
                    <option value={0}>Select LGA</option>
                    {lgas.map((lga) => (
                        <option key={lga.value} value={lga.value}>
                            {lga.name}
                        </option>
                    ))}
                </select>
                {isLoadingLgas && <p className="loading-message">Loading LGAs...</p>}
            </div>
            <div className="form-group">
                <label htmlFor="dateEstablished">Date Established</label>
                <input type="date" id="dateEstablished" name="dateEstablished" value={formData?.dateEstablished || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
                <label htmlFor="mandate">Mandate</label>
                <textarea id="mandate" name="mandate" value={formData?.mandate || ''} onChange={handleChange}></textarea>
            </div>
            <div className="form-group">
                <label htmlFor="rcNumber">RC Number</label>
                <input type="text" id="rcNumber" name="rcNumber" value={formData?.rcNumber || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input type="tel" id="phone" name="phone" value={formData?.phone || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" value={formData?.email || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
                <label htmlFor="website">Website</label>
                <input type="url" id="website" name="website" value={formData?.website || ''} onChange={handleChange} placeholder="https://www.example.com" />
            </div>

            {/* Image Uploaders */}
            <div className="image-upload-sections">
                <ImageUploader
                    label="Company Logo"
                    imageUrl={uploadedLogoUrl || formData?.logoPath}
                    onImageChange={(file) => handleFileUploadChange(file, 'logo')}
                    onRemoveImage={() => handleRemoveImage('logo')}
                    isLoading={isLogoUploading}
                    progress={logoUploadProgress}
                    error={logoUploadError}
                />
                <ImageUploader
                    label="CAC Document"
                    imageUrl={uploadedCacUrl || formData?.cacPath}
                    onImageChange={(file) => handleFileUploadChange(file, 'cac')}
                    onRemoveImage={() => handleRemoveImage('cac')}
                    isLoading={isCacUploading}
                    progress={cacUploadProgress}
                    error={cacUploadError}
                />
                 <ImageUploader
                    label="Letterhead Image"
                    imageUrl={uploadedLetterHeadUrl || formData?.logoBase64}
                    onImageChange={(file) => handleFileUploadChange(file, 'letterhead')}
                    onRemoveImage={() => handleRemoveImage('letterhead')}
                    isLoading={letterHeadUploading}
                    progress={letterHeadProgress}
                    error={letterHeadUploadError}
                />
            </div>
            <button type="submit" disabled={isLogoUploading || isCacUploading}>
                Save Organization Details
            </button>
        </form>
    );
};

export default OrgDetailsTab;